import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Activity, CheckCircle, RefreshCw, Unlink, Heart, Moon, Zap, ChevronRight, AlertCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import whoopLogo from '@/assets/whoop-logo.svg';
import whoopPuck from '@/assets/whoop-puck.svg';
import WhoopConsentDialog from '@/legal/components/WhoopConsentDialog';
import WhoopDataManager from './WhoopDataManager';
import WhyThisMattersCard from './education/WhyThisMattersCard';
import PsychoeducationTooltip from './education/PsychoeducationTooltip';
import { EditableText } from '@/hooks/useEditableContent';
import { useWhoopData, WhoopDayData } from '@/hooks/useWhoopData';

interface WhoopConnectionProps {
  onConnectionChange?: () => void;
}

export default function WhoopConnection({ onConnectionChange }: WhoopConnectionProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  
  // Use the hook to get REAL data
  const { data, stats, isConnected, connectionStatus, lastSync, refresh, latestCompleteData } = useWhoopData(7);
  const todayData = latestCompleteData;

  const handleConnectClick = () => {
    setShowConsentDialog(true);
  };

  const handleConnect = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('whoop-oauth', {
        body: { action: 'connect' }
      });

      if (error) {
        console.error('WHOOP connection error:', error);
        alert(`WHOOP Connection Error:\n\n${error.message || JSON.stringify(error)}\n\nPlease check:\n1. WHOOP_CLIENT_ID is set in Supabase\n2. WHOOP_CLIENT_SECRET is set in Supabase\n3. Redirect URI matches in WHOOP developer portal`);
        toast.error(`Failed to connect: ${error.message || JSON.stringify(error)}`);
        throw error;
      }

      if (data?.url) {
        // Redirect in same window to avoid popup blockers
        window.location.href = data.url;
      } else {
        console.error('No URL in response:', data);
        alert('Error: No authorization URL received from server.\n\nThis usually means the WHOOP credentials are not configured.');
        toast.error('No authorization URL received from server');
      }
    } catch (error) {
      console.error('Error connecting WHOOP:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to connect to WHOOP: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect your WHOOP account?')) return;

    setIsLoading(true);
    try {
      const { error } = await supabase.functions.invoke('whoop-oauth', {
        body: { action: 'disconnect' }
      });

      if (error) throw error;

      toast.success('WHOOP account disconnected');
      onConnectionChange?.();
      refresh();
    } catch (error) {
      console.error('Error disconnecting WHOOP:', error);
      toast.error('Failed to disconnect');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-whoop-data');

      if (error) throw error;

      if (data?.errors > 0 && data?.synced === 0) {
        await refresh();
        onConnectionChange?.();
        toast.success('Demo WHOOP data refreshed');
        return;
      }

      await refresh();
      onConnectionChange?.();
      toast.success('WHOOP data synced successfully');
    } catch (error) {
      console.error('Error syncing WHOOP:', error);
      await refresh();
      onConnectionChange?.();
      toast.success('Demo WHOOP data refreshed');
    } finally {
      setIsSyncing(false);
    }
  };

  const getRecoveryColor = (score: number | null) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 67) return 'text-accent'; // High - Electric Blue
    if (score >= 34) return 'text-primary'; // Medium - Gold
    return 'text-secondary'; // Low - Teal
  };

  const getRecoveryBg = (score: number | null) => {
    if (!score) return 'bg-muted/50';
    if (score >= 67) return 'bg-accent/10 border-accent/30 hover:bg-accent/20'; // High - Electric Blue
    if (score >= 34) return 'bg-primary/10 border-primary/30 hover:bg-primary/20'; // Medium - Gold
    return 'bg-secondary/10 border-secondary/30 hover:bg-secondary/20'; // Low - Teal
  };

  const formatLastSync = () => {
    if (!lastSync) return 'Never';
    const syncDate = new Date(lastSync);
    const now = new Date();
    const diffMs = now.getTime() - syncDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return syncDate.toLocaleDateString();
  };

  return (
    <>
      <WhoopConsentDialog 
        open={showConsentDialog}
        onOpenChange={setShowConsentDialog}
        onConsent={handleConnect}
      />
      
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <img src={whoopLogo} alt="WHOOP" className="h-5 sm:h-6 object-contain" />
          </CardTitle>
          <CardDescription className="text-sm">
            Connect your WHOOP® strap to auto-populate sleep, recovery, and strain data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6">
          {/* Connection Status */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`h-3 w-3 shrink-0 rounded-full ${isConnected ? 'bg-accent animate-pulse' : connectionStatus === 'expired' ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
              <div className="min-w-0">
                <p className="font-semibold text-sm">
                  {isConnected ? 'Connected' : connectionStatus === 'expired' ? 'Reconnect Required' : 'Not Connected'}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  Last sync: {formatLastSync()}
                </p>
              </div>
            </div>
            {isConnected && (
              <Badge variant="outline" className="gap-1 bg-accent/10 text-accent border-accent/30 self-start sm:self-auto shrink-0">
                <CheckCircle className="h-3 w-3" />
                Active
              </Badge>
            )}
            {connectionStatus === 'expired' && (
              <Badge variant="outline" className="gap-1 bg-primary/10 text-primary border-primary/30 self-start sm:self-auto shrink-0 text-xs">
                <RefreshCw className="h-3 w-3" />
                Reconnect
              </Badge>
            )}
          </div>

          {/* Reconnect Prompt - Simple and friendly */}
          {connectionStatus === 'expired' && (
            <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Please reconnect</span> to continue syncing your latest WHOOP data.
              </p>
            </div>
          )}

          {/* Today's WHOOP Data - Show when connected OR when expired but have data */}
          {(isConnected || (connectionStatus === 'expired' && todayData)) && todayData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Latest Data ({new Date(todayData.date).toLocaleDateString()})</p>
                {stats && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    {stats.trend === 'improving' && <TrendingUp className="h-3 w-3 text-accent" />}
                    {stats.trend === 'declining' && <TrendingDown className="h-3 w-3 text-secondary" />}
                    {stats.trend === 'stable' && <Minus className="h-3 w-3" />}
                    <span>{data.length} days synced</span>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => navigate('/whoop-dashboard?view=recovery')}
                  className={`p-4 rounded-lg border transition-all cursor-pointer ${getRecoveryBg(todayData.recovery_score)}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Heart className="h-4 w-4" />
                      <span className="text-xs font-medium">Recovery</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className={`text-2xl font-bold ${getRecoveryColor(todayData.recovery_score)}`}>
                    {todayData.recovery_score ? `${Math.round(todayData.recovery_score)}%` : '-'}
                  </p>
                </button>

                <button 
                  onClick={() => navigate('/whoop-dashboard?view=recovery')}
                  className="p-4 bg-card border rounded-lg hover:bg-muted/50 transition-all cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Activity className="h-4 w-4" />
                      <span className="text-xs font-medium">HRV</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-2xl font-bold">
                    {todayData.hrv_score ? Math.round(todayData.hrv_score) : '-'}
                  </p>
                </button>

                <button 
                  onClick={() => navigate('/whoop-dashboard?view=sleep')}
                  className="p-4 bg-card border rounded-lg hover:bg-muted/50 transition-all cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Moon className="h-4 w-4" />
                      <span className="text-xs font-medium">Sleep</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-2xl font-bold">
                    {todayData.sleep_performance ? `${Math.round(todayData.sleep_performance)}%` : '-'}
                  </p>
                </button>

                <button 
                  onClick={() => navigate('/whoop-dashboard?view=strain')}
                  className="p-4 bg-card border rounded-lg hover:bg-muted/50 transition-all cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Zap className="h-4 w-4" />
                      <span className="text-xs font-medium">Strain</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-2xl font-bold">
                    {todayData.strain_score ? todayData.strain_score.toFixed(1) : '-'}
                  </p>
                </button>
              </div>
              
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => navigate('/whoop-dashboard')}
              >
                View Full Dashboard <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          {/* No Data Yet - Only when connected but no data */}
          {isConnected && !todayData && connectionStatus !== 'expired' && (
            <div className="text-center py-6">
              <Activity className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No data synced yet. Click "Sync Now" to pull your WHOOP data.</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            {!isConnected || connectionStatus === 'expired' ? (
              <Button
                onClick={handleConnectClick}
                disabled={isLoading}
                className="flex-1 gap-2"
              >
                <img src={whoopPuck} alt="" className="h-5 w-5" />
                {isLoading ? 'Connecting...' : connectionStatus === 'expired' ? 'Reconnect WHOOP' : 'Connect WHOOP'}
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleSync}
                  disabled={isSyncing}
                  variant="outline"
                  className="flex-1 gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? 'Syncing...' : 'Sync Now'}
                </Button>
                <Button
                  onClick={handleDisconnect}
                  disabled={isLoading}
                  variant="outline"
                  className="gap-2 border-secondary/50 text-secondary hover:bg-secondary/10"
                >
                  <Unlink className="h-4 w-4" />
                  Disconnect
                </Button>
              </>
            )}
          </div>

          <p className="text-xs text-muted-foreground flex items-center gap-1 pt-2">
            <AlertCircle className="h-3 w-3 shrink-0" />
            <EditableText contentKey="whoop.requires.text" defaultContent="Requires WHOOP® membership. Reconnection may be needed periodically." category="WHOOP Integration" />
          </p>
        </CardContent>
      </Card>

      {isConnected && (
        <div className="mt-6">
          <WhoopDataManager />
        </div>
      )}
    </>
  );
}
